<template>
  <b-modal
    id="ai-optimize-review-modal"
    ref="modal"
    title="Review AI Optimizations"
    size="xl"
    @hidden="onHidden"
    :hide-footer="true"
    scrollable
    centered
  >
    <div class="ai-review-container">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-container">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
        <h4 class="mt-3">Optimizing your content with AI...</h4>
        <p class="text-muted">This may take a moment. We're analyzing your entry and generating professional suggestions.</p>
      </div>
      
      <!-- Header with stats -->
      <div v-else class="review-header">
        <div class="stats">
          <span class="stat-item">
            <strong>{{ changeCount }}</strong> field{{ changeCount !== 1 ? 's' : '' }} optimized
          </span>
          <span class="stat-item">
            <strong>{{ appliedCount }}</strong> applied
          </span>
          <span class="stat-item">
            <strong>{{ pendingCount }}</strong> pending
          </span>
        </div>
      </div>

      <!-- Changes list -->
      <div v-if="!isLoading" class="changes-list">
        <div
          v-for="change in changes"
          :key="change.fieldKey"
          class="change-item"
          :class="{ 'applied': change.applied, 'rejected': change.rejected }"
        >
          <!-- Field header -->
          <div class="change-header">
            <div class="field-info">
              <h5 class="field-name">{{ change.fieldLabel }}</h5>
              <span v-if="change.applied" class="badge badge-success">Applied</span>
              <span v-else-if="change.rejected" class="badge badge-secondary">Skipped</span>
            </div>
            <div class="change-actions">
              <b-button
                v-if="!change.applied && !change.rejected"
                variant="success"
                size="sm"
                @click="applyChange(change)"
              >
                <i class="fa fa-check"></i> Apply
              </b-button>
              <b-button
                v-if="!change.applied && !change.rejected"
                variant="outline-secondary"
                size="sm"
                @click="rejectChange(change)"
              >
                <i class="fa fa-times"></i> Skip
              </b-button>
              <b-button
                v-if="change.applied"
                variant="outline-warning"
                size="sm"
                @click="revertChange(change)"
              >
                <i class="fa fa-undo"></i> Revert
              </b-button>
            </div>
          </div>

          <!-- Content comparison -->
          <div class="change-content">
            <div class="content-column original">
              <div class="column-label">Original</div>
              <div class="content-text" v-html="formatContent(change.originalValue)"></div>
            </div>
            <div class="content-divider">
              <i class="fa fa-arrow-right"></i>
            </div>
            <div class="content-column optimized">
              <div class="column-label">AI Optimized</div>
              <div class="content-text" v-html="formatContent(change.optimizedValue)"></div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="changes.length === 0" class="empty-state">
          <p>No changes to review</p>
        </div>
      </div>

      <!-- Footer actions -->
      <div v-if="!isLoading" class="review-footer">
        <div class="footer-actions">
          <b-button
            variant="outline-secondary"
            @click="closeModal"
          >
            Close
          </b-button>
          <b-button
            v-if="pendingCount > 0 || rejectedCount > 0"
            variant="outline-primary"
            @click="$emit('re-optimize-requested')"
          >
            <i class="fa fa-refresh"></i> Re-optimize {{ pendingCount > 0 ? 'Remaining' : 'All' }}
          </b-button>
          <b-button
            v-if="pendingCount > 0"
            variant="primary"
            @click="applyAllPending"
          >
            Apply All Remaining ({{ pendingCount }})
          </b-button>
          <b-button
            v-if="appliedCount > 0 && pendingCount === 0"
            variant="success"
            @click="confirmAndClose"
          >
            <i class="fa fa-check"></i> Done ({{ appliedCount }} applied)
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import * as AIHelper from '../../utilities/aiOptimizationHelper';

export default {
  name: 'AIOptimizeReviewModal',
  data() {
    return {
      changes: [],
      formPages: null,
      isLoading: false
    };
  },
  computed: {
    changeCount() {
      return this.changes.length;
    },
    appliedCount() {
      return this.changes.filter(c => c.applied).length;
    },
    rejectedCount() {
      return this.changes.filter(c => c.rejected).length;
    },
    pendingCount() {
      return this.changes.filter(c => !c.applied && !c.rejected).length;
    }
  },
  methods: {
    /**
     * Show the modal immediately with loading state
     */
    showLoading() {
      this.isLoading = true;
      this.changes = [];
      this.$refs.modal.show();
    },
    
    /**
     * Show the modal with results (after loading)
     * @param {Object} optimizedContent - AI optimized content
     * @param {Array} formPages - Form pages to update
     */
    showResults(optimizedContent, formPages) {
      this.formPages = formPages;
      this.changes = this.buildChangesList(optimizedContent, formPages);
      this.isLoading = false;
    },
    
    /**
     * Show the modal with optimized content (for reopening)
     * @param {Object} optimizedContent - AI optimized content
     * @param {Array} formPages - Form pages to update
     */
    show(optimizedContent, formPages) {
      this.formPages = formPages;
      this.changes = this.buildChangesList(optimizedContent, formPages);
      this.isLoading = false;
      this.$refs.modal.show();
    },
    
    /**
     * Hide the loading state and close modal (on error)
     */
    hideLoading() {
      this.isLoading = false;
      this.$refs.modal.hide();
    },

    /**
     * Build list of changes from optimized content
     * @param {Object} optimizedContent - AI optimized content
     * @param {Array} formPages - Form pages
     * @returns {Array} - List of changes
     */
    buildChangesList(optimizedContent, formPages) {
      return AIHelper.buildChangesList(optimizedContent, formPages);
    },

    /**
     * Apply a single change
     * @param {Object} change - Change to apply
     */
    applyChange(change) {
      AIHelper.applyChange(change);
      this.$emit('change-applied', change);
    },

    /**
     * Reject/skip a single change
     * @param {Object} change - Change to reject
     */
    rejectChange(change) {
      AIHelper.rejectChange(change);
    },

    /**
     * Revert an applied change
     * @param {Object} change - Change to revert
     */
    revertChange(change) {
      AIHelper.revertChange(change);
      this.$emit('change-reverted', change);
    },

    /**
     * Apply all pending changes
     */
    applyAllPending() {
      this.changes.forEach(change => {
        if (!change.applied && !change.rejected) {
          this.applyChange(change);
        }
      });
    },

    /**
     * Format content for display (handles both plain text and HTML)
     * @param {string} content - Content to format
     * @returns {string} - Formatted content
     */
    formatContent(content) {
      return AIHelper.formatContentForDisplay(content);
    },

    /**
     * Close modal with confirmation if changes applied
     */
    confirmAndClose() {
      if (this.appliedCount > 0) {
        this.$emit('changes-confirmed', this.appliedCount);
      }
      this.closeModal();
    },

    /**
     * Close the modal
     */
    closeModal() {
      this.$refs.modal.hide();
    },

    /**
     * Handle modal hidden event
     */
    onHidden() {
      // Don't reset state - persist it so user can reopen and review
      // State will only be cleared when a new optimization is performed
    },
    
    /**
     * Check if there are existing changes to show
     * @returns {boolean}
     */
    hasExistingChanges() {
      return this.changes.length > 0;
    },
    
    /**
     * Clear all stored changes (used when starting fresh optimization)
     */
    clearChanges() {
      this.changes = [];
      this.formPages = null;
    }
  }
};
</script>

<style scoped lang="scss">
.ai-review-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 70vh;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  
  h4 {
    color: #495057;
    font-weight: 600;
  }
  
  p {
    max-width: 500px;
  }
}

.review-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
  
  .stats {
    display: flex;
    gap: 2rem;
    
    .stat-item {
      color: #6c757d;
      
      strong {
        color: #495057;
        font-size: 1.1rem;
      }
    }
  }
}

.changes-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.change-item {
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  background: #fff;
  transition: all 0.2s ease;
  
  &.applied {
    border-color: #28a745;
    background: #f0f9f4;
  }
  
  &.rejected {
    border-color: #dee2e6;
    background: #f8f9fa;
    opacity: 0.7;
  }
}

.change-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  .field-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .field-name {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #495057;
    }
    
    .badge {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }
  }
  
  .change-actions {
    display: flex;
    gap: 0.5rem;
    
    button {
      font-size: 0.875rem;
      padding: 0.375rem 0.75rem;
      
      i {
        margin-right: 0.25rem;
      }
    }
  }
}

.change-content {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  align-items: start;
}

.content-column {
  min-width: 0; // Allow content to shrink
  
  .column-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .content-text {
    padding: 0.75rem;
    border-radius: 4px;
    background: #f8f9fa;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #495057;
    white-space: pre-wrap;
    word-wrap: break-word;
    max-height: 300px;
    overflow-y: auto;
  }
  
  &.original .content-text {
    background: #fff5f5;
    border: 1px solid #ffc9c9;
  }
  
  &.optimized .content-text {
    background: #f0f9ff;
    border: 1px solid #b3e0ff;
  }
}

.content-divider {
  display: flex;
  align-items: center;
  padding-top: 2rem;
  color: #adb5bd;
  font-size: 1.25rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  
  p {
    margin: 0;
    font-size: 1.1rem;
  }
}

.review-footer {
  padding-top: 1rem;
  border-top: 2px solid #e9ecef;
  
  .footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
}

// Responsive design
@media screen and (max-width: 768px) {
  .change-content {
    grid-template-columns: 1fr;
    
    .content-divider {
      padding: 0;
      justify-content: center;
      
      i {
        transform: rotate(90deg);
      }
    }
  }
  
  .change-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    
    .change-actions {
      width: 100%;
      
      button {
        flex: 1;
      }
    }
  }
  
  .review-header .stats {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
